import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import moment from 'moment-jalali';
import Calendar from './Calendar';
import { remove as removeIcon } from '../utils/assets';

const Wrapper = styled.div`
  border-radius: 3px;
  box-shadow: 0 3px 10px #dbdbdb;
  border: 1px solid #cccccc;
  width: 300px;
  margin: auto;
  text-align: center;
  padding: 10px;
  background-color: #fff;

  .day-picker-container:after,
  .months-list:after,
  .days-of-week:after {
    content: '';
    display: block;
    clear: both;
  }

  .heading {
    height: 42px;
    font-weight: bold;
    margin-bottom: 10px;

    > button {
      border-radius: 3px;
      background: none;
      margin: 5px 0;
      border: 1px solid #f7f7f7;
      text-align: center;
      line-height: 30px;
      min-width: 36px;
      height: 32px;
      cursor: pointer;

      &:hover {
        background-color: #f2f2f2;
      }
    }

    > span {
      line-height: 35px;
    }

    svg {
      width: 10px;
    }

    .prev {
      float: right;
    }

    .next {
      float: left;
    }

    .title {
      line-height: 32px;
      width: 120px;
      height: 32px;
      font-size: 1em;
      margin: 5px 0;
      border: 1px solid #f7f7f7;
      text-align: center;
      display: inline-block;
      font-weight: normal;
    }
  }

  .days-of-week {
    border-bottom: 1px solid #eee;
    margin-bottom: 5px;
    padding-bottom: 5px;
    display: flex;
    width: 100%;

    > div {
      flex-grow: 1;
      justify-content: space-between;
    }
  }

  .day-wrapper {
    padding: 5px;
    float: right;
    width: 14.28571429%;

    button {
      border: none;
      background: none;
      outline: none;
      width: 100%;
      height: 36px;
      border-radius: 3px;
      cursor: pointer;
    }

    button:hover {
      background-color: #eeeeff;
    }

    button[disabled] {
      color: #aaa;
      cursor: not-allowed;
      background-color: #ebebeb;
    }

    button.selected {
      background-color: #337ab7;
      color: #ffffff;
    }

    &:not(.currentMonth) button {
      opacity: 0.5;
    }
  }

  .months-list {
    clear: both;
    width: 100%;

    button {
      width: 33.33333332%;
      height: 25%;
      float: right;
      border: 1px solid #f9f9f9;
      outline: none;
      font-size: 1em;
      background: #fff;
      padding: 10px 0;
      cursor: pointer;
    }

    button:hover {
      background: #eeeeee;
      cursor: pointer;
    }
  }

  .selected {
    button,
    button:hover,
    button:active,
    button:focus {
      background-color: #337ab7;
      color: #ffffff;
    }
  }
`;

const styles = {
  wrapperVisible: {
    position: 'relative'
  },
  wrapperHidden: {
    position: 'relative',
    overflow: 'hidden'
  },
  calendarHidden: {
    visibility: 'hidden',
    opacity: 0,
    transform: 'translateY(10px)'
  },
  calendarVisible: {
    visibility: 'visible',
    opacity: 1,
    transform: 'translateY(0)'
  },
  calendar: {
    position: 'absolute',
    top: '100%',
    transition: 'all .3s ease',
    zIndex: '9999'
  }
};

export default class DatePicker extends React.Component {
  static propTypes = {
    value: PropTypes.object,
    onChange: PropTypes.func,
    children: PropTypes.node,
    min: PropTypes.object,
    max: PropTypes.object,
    defaultMonth: PropTypes.object,
    displayFormat: PropTypes.string,
    removable: PropTypes.bool,
    selectedDays: PropTypes.array,
    disableDays: PropTypes.array
  };

  static defaultProps = {
    displayFormat: 'jYYYY/jMM/jDD'
  };

  state = {
    visible: false,
    value: this.props.value,
    inputValue:
      this.props.value && typeof this.props.value === 'object' && this.props.value._isAMomentObject
        ? this.props.value.format(this.props.displayFormat)
        : ''
  };

  componentWillReceiveProps(nextProps) {
    if (this.props.value !== nextProps.value) {
      this.handleChange(nextProps.value, false, true);
    }
  }

  setVisibility(visible) {
    if (visible) {
      this.closing = true;
    }

    this.setState({ visible });
  }

  handleInputChange(event) {
    const { onChange, displayFormat } = this.props;
    const inputValue = event.target.value;
    const dateTime = moment(inputValue, displayFormat);
    this.setState({ inputValue });
    if (this.handleChange(dateTime, true)) {
      this.setState({ value: dateTime });
    } else if (inputValue === '') {
      onChange(null);
    }
  }

  handleChange(date, preserveInput = false, callChange = true) {
    const { onChange, displayFormat } = this.props;

    if (date.isValid()) {
      if (!preserveInput) {
        this.setState({
          inputValue: date.format(displayFormat)
        });
      }

      if (onChange && callChange) {
        onChange(date);
      }

      return true;
    }
  }

  handleSelect(date) {
    this.setVisibility(false);
    this.handleChange(date);
  }

  handleInputFocus(e) {
    e.stopPropagation();
    this.setVisibility(true);
  }

  handleInputBlur() {
    setTimeout(() => {
      if (this.closing) {
        this.setVisibility(false);
      } else {
        this.closing = true;
      }
    }, 200);
  }

  removeDate() {
    const { onChange } = this.props;
    if (onChange) {
      onChange('');
    }
    this.setState({
      input: '',
      inputValue: ''
    });
  }

  render() {
    const { visible, inputValue } = this.state;
    const {
      min,
      max,
      defaultMonth,
      removable,
      children,
      selectedDays,
      disableDays,
      value,
      ...rest
    } = this.props;

    const calendarVisibilityStyle = visible ? styles.calendarVisible : styles.calendarHidden;
    const calendarStyles = {
      ...styles.calendar,
      ...calendarVisibilityStyle
    };

    const wrapperStyles = visible ? styles.wrapperVisible : styles.wrapperHidden;

    return (
      <div style={wrapperStyles} className="calendar-datepicker">
        <input
          type="text"
          {...rest}
          onChange={this.handleInputChange.bind(this)}
          onFocus={this.handleInputFocus.bind(this)}
          onBlur={this.handleInputBlur.bind(this)}
          value={inputValue}
          ref="formatter"
        />

        {removable && inputValue ? (
          <button
            className="remove-date"
            type="button"
            onClick={this.removeDate.bind(this)}
            dangerouslySetInnerHTML={removeIcon}
          />
        ) : null}

        {children}

        <Wrapper
          style={calendarStyles}
          onClick={() => {
            this.closing = false;
          }}
        >
          <Calendar
            selectedDay={value}
            min={min}
            max={max}
            defaultMonth={defaultMonth}
            selectedDays={selectedDays}
            disableDays={disableDays}
            onSelect={this.handleSelect.bind(this)}
          />
        </Wrapper>
      </div>
    );
  }
}
